/**
 * Time Utility Functions
 *
 * Helper functions for working with time values in the timeline.
 * Times are stored in seconds but displayed in various formats.
 */

/**
 * Format seconds as MM:SS or HH:MM:SS
 *
 * @param seconds - Time in seconds
 * @param includeHours - Whether to always include hours
 * @returns Formatted time string
 *
 * @example
 * formatTime(65) // "01:05"
 * formatTime(3665) // "1:01:05"
 * formatTime(5, true) // "0:00:05"
 */
export function formatTime(seconds: number, includeHours = false): string {
  // Handle edge cases
  if (!isFinite(seconds) || seconds < 0) {
    return '00:00';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  // Pad with zeros
  const paddedMinutes = minutes.toString().padStart(2, '0');
  const paddedSeconds = secs.toString().padStart(2, '0');

  if (hours > 0 || includeHours) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }

  return `${paddedMinutes}:${paddedSeconds}`;
}

/**
 * Format seconds with milliseconds (for precise display)
 *
 * @param seconds - Time in seconds
 * @returns Formatted time string with deciseconds
 *
 * @example
 * formatTimePrecise(65.5) // "01:05.5"
 */
export function formatTimePrecise(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) {
    return '00:00.0';
  }

  const wholeSeconds = Math.floor(seconds);
  const deciseconds = Math.floor((seconds - wholeSeconds) * 10);
  const base = formatTime(wholeSeconds);

  return `${base}.${deciseconds}`;
}

/**
 * Format seconds with frames based on project FPS
 *
 * @param seconds - Time in seconds
 * @param fps - Frames per second
 * @returns Formatted time string MM:SS:FF
 *
 * @example
 * formatTimeWithFrames(65.5, 24) // "01:05:12"
 */
export function formatTimeWithFrames(seconds: number, fps: number): string {
  if (!isFinite(seconds) || seconds < 0) {
    return '00:00:00';
  }

  const totalFrames = Math.floor(seconds * fps);
  const frameNumber = totalFrames % fps;
  const totalSeconds = Math.floor(seconds);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  // Pad with zeros
  const paddedMinutes = minutes.toString().padStart(2, '0');
  const paddedSeconds = secs.toString().padStart(2, '0');
  const paddedFrames = frameNumber.toString().padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}:${paddedFrames}`;
  }

  return `${paddedMinutes}:${paddedSeconds}:${paddedFrames}`;
}

/**
 * Parse a time string to seconds
 *
 * @param timeString - Time string in MM:SS or HH:MM:SS format
 * @returns Time in seconds, or 0 if invalid
 *
 * @example
 * parseTime("01:30") // 90
 * parseTime("1:30:00") // 5400
 */
export function parseTime(timeString: string): number {
  const parts = timeString.split(':').map((p) => parseInt(p, 10));

  if (parts.some(isNaN)) {
    return 0;
  }

  if (parts.length === 2) {
    // MM:SS
    const [minutes, seconds] = parts;
    return minutes * 60 + seconds;
  }

  if (parts.length === 3) {
    // HH:MM:SS
    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  }

  return 0;
}

/**
 * Convert frames to seconds
 *
 * @param frames - Number of frames
 * @param fps - Frames per second
 * @returns Time in seconds
 */
export function framesToSeconds(frames: number, fps: number): number {
  if (fps <= 0) return 0;
  return frames / fps;
}

/**
 * Convert seconds to frames
 *
 * @param seconds - Time in seconds
 * @param fps - Frames per second
 * @returns Number of frames (floored)
 */
export function secondsToFrames(seconds: number, fps: number): number {
  if (fps <= 0) return 0;
  return Math.floor(seconds * fps);
}

/**
 * Clamp a time value to a valid range
 *
 * @param time - Time to clamp
 * @param min - Minimum value (default 0)
 * @param max - Maximum value
 * @returns Clamped time value
 */
export function clampTime(time: number, min: number = 0, max: number): number {
  return Math.max(min, Math.min(time, max));
}

/**
 * Calculate estimated reading time for text
 *
 * Based on average reading speed of ~150 words per minute for careful reading
 * (slower than typical reading to account for screenplay delivery)
 *
 * @param text - The text to estimate
 * @param wordsPerMinute - Reading speed (default 150)
 * @returns Estimated time in seconds
 */
export function estimateReadingTime(
  text: string,
  wordsPerMinute: number = 150
): number {
  if (!text || !text.trim()) {
    return 0;
  }

  const wordCount = text.trim().split(/\s+/).length;
  const minutes = wordCount / wordsPerMinute;

  // Return at least 1 second, rounded to nearest half-second
  return Math.max(1, Math.round(minutes * 60 * 2) / 2);
}
